import { Dispatch, FC, SetStateAction, useState } from 'react';
import { ArrayField, UseFormMethods } from 'react-hook-form';
import { DsmGrid } from '@dsm-dcs/design-system-react';
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { FormType } from "../../common";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import {
	DairyV2ContinuousGrowingStageData,
	DairyV2DairyCowStageData,
	DairyV2Intervention,
	DairyV2Stage
} from '../../../../models/Intervention/DairyV2Intervention';
import { StageType } from '../../../../../../graphql/types';
import DefinitionsDialog from '../../CommonDataParts/v2.0/DefinitionsDialog';
import lookupValues from '../../../../helpers/lookupValues';
import DairyV2HousingComponent from './DairyV2HousingComponent';
import DairyV2InterventionMMSComponent from './DairyV2InterventionMMSComponent';
import DairyV2InterventionBeddingComponent from './DairyV2InterventionBeddingComponent';
import EditModeButtons from '../../../helpers/EditModeButtons';

export interface DairyV2HousingAndManureFormProps {
	formContext: UseFormMethods<DairyV2Intervention>;
	formType: FormType;
	stageIndex: number;
	baseline: DairyV2Stage;
	stageType: string;
	fieldItemPrefix: string;
	dairyStageData: DairyV2ContinuousGrowingStageData | DairyV2DairyCowStageData;
	beddingSystemsCollection: {
		isBeddingEdit: boolean;
		setIsBeddingEdit: Dispatch<SetStateAction<boolean>>;
		beddingSystemsAdditions: Partial<ArrayField<Record<string, any>, "keyId">>[];
		appendBeddingSystems: (value: Partial<Record<string, any>> | Partial<Record<string, any>>[], shouldFocus?: boolean) => void;
		removeBeddingSystems: (index?: number | number[]) => void;
		beddingCancelHandler: (isInnerCancel?: boolean) => void;
	}
	mmsCollection: {
		mmsAdditions: Partial<ArrayField<Record<string, any>, "keyId">>[];
		appendMMSAdditions: (value: Partial<Record<string, any>> | Partial<Record<string, any>>[], shouldFocus?: boolean) => void;
		removeMMSAdditions: (index?: number | number[]) => void;
	}
	timeSpentCollection: {
		isTimeSpentEdit: boolean;
		setIsTimeSpentEdit: Dispatch<SetStateAction<boolean>>;
		timeSpentCancelHandler: (isInnerCancel?: boolean) => void;
	}
}

const DairyV2HousingAndManureForm: FC<DairyV2HousingAndManureFormProps> = ({
	formContext,
	formType = FormType.Add,
	stageIndex = 0,
	baseline,
	stageType,
	fieldItemPrefix,
	dairyStageData,
	beddingSystemsCollection,
	mmsCollection,
	timeSpentCollection,
}) => {
	const intl = useIntl();
	const classes = processAndStageStylesV2() as CSSClassesList;
	const { isTimeSpentEdit, setIsTimeSpentEdit, timeSpentCancelHandler } = timeSpentCollection;

	const [isDescriptionDialogVisible, setIsDescriptionDialogVisible] = useState<boolean>(false);

	return (
		<div style={{ paddingTop: 0 }}>
			<input
				ref={formContext.register()}
				type="hidden"
				name={`${fieldItemPrefix}.baselineTimeInHousing`}
				value={Number(dairyStageData?.housing.timeInHousing)}
			/>
			<input
				ref={formContext.register()}
				type="hidden"
				name={`${fieldItemPrefix}.baselineTimeInFeedlot`}
				value={Number(dairyStageData?.housing.timeInFeedlot)}
			/>
			<input
				ref={formContext.register()}
				type="hidden"
				name={`${fieldItemPrefix}.baselineTimeGrazingLargeAreas`}
				value={Number(dairyStageData?.housing.timeGrazingLargeAreas)}
			/>
			<input
				ref={formContext.register()}
				type="hidden"
				name={`${fieldItemPrefix}.baselineTimeGrazingPastures`}
				value={Number(dairyStageData?.housing.timeGrazingPastures)}
			/>
			{isDescriptionDialogVisible && (
				<DefinitionsDialog
					open
					prefix="BASELINE.FORM.INPUT.CATTLE.MMS"
					itemHeader="BASELINE.FORM.BARN.MANURE_MANAGEMENT_SYSTEM"
					items={lookupValues.cattleMMSDefinitions}
					handleClose={() => setIsDescriptionDialogVisible(false)}
				/>
			)}

			<p className={classes.headingTitleVariant}>
				{intl.formatMessage({
					id: "SUSTELL.STAGE.POULTRY.HOUSING.BEDDING_TYPE",
				})}
			</p>
			<DsmGrid className={classes.dsmGridTwoColumn}>
				<DsmGrid
					className={classes.dsmGridOneColumnWithBorder}
					style={{ padding: 'var(--dsm-spacing-px-4)' }}
				>
					<DairyV2InterventionBeddingComponent
						formType={formType}
						stageIndex={stageIndex}
						baseline={baseline}
						fieldItemPrefix={fieldItemPrefix}
						beddingSystemsCollection={beddingSystemsCollection}
					/>
				</DsmGrid>
			</DsmGrid>

			<p className={classes.headingTitleVariant} style={{ marginTop: 'var(--dsm-spacing-px-4)' }}>
				{intl.formatMessage({
					id: "SUSTELL.STAGE.DAIRY.MMS.DESCRIPTION",
				})}
			</p>
			<DsmGrid className={!isTimeSpentEdit ? classes.dsmGridTwoColumn2nd45NoGap : classes.dsmGridTwoColumn2nd70NoGap}>
				<DsmGrid className={classes.dsmGridOneColumnWithBorder} style={{ padding: 0, gap: 0 }}>
					<DairyV2HousingComponent
						stageIndex={stageIndex}
						isViewMode={!isTimeSpentEdit}
						isDairyCowStage={stageType === StageType.DairyCow}
						formType={formType}
						baseline={dairyStageData.housing}
						fieldItemPrefix={fieldItemPrefix}
					/>
					{formType !== FormType.View &&
						<div style={{ marginLeft: '8px', marginBottom: "8px" }}>
							<EditModeButtons
								isEditing={isTimeSpentEdit}
								onEdit={() => setIsTimeSpentEdit(true)}
								onCancel={timeSpentCancelHandler}
							/>
						</div>}
				</DsmGrid>

				<div className={classes.dsmGridOneColumnLightBlueBoxContainer}>
					<DairyV2InterventionMMSComponent
						formContext={formContext}
						formType={formType}
						stageIndex={stageIndex}
						openDescriptionDialog={() => setIsDescriptionDialogVisible(true)}
						isViewMode={!isTimeSpentEdit}
						isDairyCowStage={stageType === StageType.DairyCow}
						fieldItemPrefix={fieldItemPrefix}
						baseline={dairyStageData.housing}
						mmsCollection={mmsCollection}
					/>
				</div>
			</DsmGrid>
		</div>
	);
};

export default DairyV2HousingAndManureForm;
